import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useLocale } from './LangContext';
import { getRoleLabels } from './roleLabels';
import { useAuth } from './AuthContext';
import '../styles/Members.css';

const TITLE_ORDER = {
  founder: 7,
  chu_nhiem: 6,
  pho_chu_nhiem: 5,
  truong_ban_dao_tao: 4,
  truong_ban_su_kien: 3,
  pho_ban_dao_tao: 2,
  pho_ban_su_kien: 1,
};

const BAN_PRIORITY = ['ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien', 'ky_thuat'];
const VISIBLE_BANS = ['ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien'];

const CTF_FIELD_MAP = {
  reverse_engineering: 'Reverse Engineering',
  pwnable: 'Binary Exploitation',
  web_exploitation: 'Web Exploitation',
  cryptography: 'Cryptography',
  forensics: 'Digital Forensics',
  osint: 'Open Source Intelligence',
};

const pickPrimaryBan = (roleRaw) => {
  const roles = Array.isArray(roleRaw) ? roleRaw : (roleRaw ? [roleRaw] : []);
  for (const key of BAN_PRIORITY) {
    if (roles.includes(key)) return key;
  }
  return null;
};

const pickAllBans = (roleRaw) => {
  const roles = Array.isArray(roleRaw) ? roleRaw : (roleRaw ? [roleRaw] : []);
  return VISIBLE_BANS.filter(k => roles.includes(k));
};

// Module-level caches survive component unmount (e.g. navigating to a member
// profile and pressing Back), so the list renders instantly and the per-gen
// More/Less state is preserved instead of collapsing to a broken-looking view.
let _membersGroupsCache = null;
const _genExpandedCache = {};

const MemberAvatar = ({ member }) => {
  // Build the slideshow list: prefer the full photos[] (avatar + album) from the
  // server; fall back to the single avatarImg for older payloads.
  const list = (Array.isArray(member.photos) && member.photos.length > 0)
    ? member.photos
    : (member.avatarImg ? [member.avatarImg] : []);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (list.length <= 1) return undefined;
    const t = setInterval(() => setIdx(i => (i + 1) % list.length), 4000);
    return () => clearInterval(t);
  }, [list.length]);
  if (list.length === 0) return null;
  return (
    <div className="member-photo-carousel">
      {list.map((img, i) => (
        <img
          key={i}
          src={`/uploads/profiles/${img}`}
          alt=""
          loading="lazy"
          decoding="async"
          className={`member-photo ${i === idx ? 'active' : ''}${list.length === 1 ? ' static' : ''}`}
        />
      ))}
    </div>
  );
};

const InitialsAvatar = ({ name, index }) => {
  const colors = [
    'linear-gradient(135deg, #0d9488, #00ff41)',
    'linear-gradient(135deg, #06b6d4, #0d9488)',
    'linear-gradient(135deg, #3b82f6, #06b6d4)',
    'linear-gradient(135deg, #8b5cf6, #3b82f6)',
    'linear-gradient(135deg, #ec4899, #8b5cf6)',
    'linear-gradient(135deg, #f59e0b, #ef4444)',
  ];
  const initials = (name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div
      className="member-initials-avatar"
      style={{ background: colors[index % colors.length] }}
    >
      <span>{initials}</span>
    </div>
  );
};

const MemberCard = ({ member, index, inView }) => {
  const { locale } = useLocale();
  const { isAuthenticated } = useAuth();
  // Guests see role labels in English by default.
  const L = getRoleLabels(isAuthenticated ? locale : 'en');
  const POSITION_MAP = L.position;
  const TITLE_MAP = L.title;
  const BAN_LABEL = L.ban;
  const hasDisplayPhotos = !!(member.avatarImg || member.avatar || (Array.isArray(member.photos) && member.photos.length));

  const allBans = pickAllBans(member.role);
  const showTitle = member.title && TITLE_MAP[member.title];
  const showBans = !showTitle && allBans.length > 0;
  const showPosition = !showTitle && !showBans && member.position;
  const isFounder = member.position === 'founder' || member.title === 'founder';

  return (
    <motion.div
      className="member-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: Math.min(index, 12) * 0.04, duration: 0.4 }}
      whileHover={{ y: -6 }}
    >
      <Link to={`/isper/${member.username}`} className="member-card-link">
        <div className="member-photo-area">
          {hasDisplayPhotos ? (
            <MemberAvatar member={member} />
          ) : (
            <InitialsAvatar name={member.fullName} index={index} />
          )}
        </div>
        <div className="member-info">
          <h3 className="member-name">{member.fullName}</h3>
          {showTitle && (
            <span className={`member-role-label member-role-${member.title}`}>{TITLE_MAP[member.title]}</span>
          )}
          {showBans && allBans.map(b => (
            <span key={b} className="member-ban-label">{BAN_LABEL[b]}</span>
          ))}
          {isFounder && (
            <span className="member-founder-badge" title="Người sáng lập">
              <span className="founder-star">★</span>
              <span>Founder</span>
            </span>
          )}
          {showPosition && (
            <span className="member-position">
              {POSITION_MAP[member.position] || member.position}
            </span>
          )}
          {member.ctfField && (
            <span className="member-ctf-field">
              {CTF_FIELD_MAP[member.ctfField] || member.ctfField}
            </span>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

const GenSection = ({ group, inView, startIndex }) => {
  const { gen, leaders, others } = group;
  const [expanded, setExpanded] = useState(() => !!_genExpandedCache[gen]);
  const toggleExpanded = () => setExpanded((v) => {
    const next = !v;
    _genExpandedCache[gen] = next;
    return next;
  });
  let idx = startIndex;

  return (
    <div className="gen-group">
      <motion.div
        className="gen-heading"
        initial={{ opacity: 0, x: -20 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.4 }}
      >
        <span className="gen-line" />
        <span className="gen-label">Gen {gen}</span>
        <span className="gen-line" />
      </motion.div>

      {leaders.length > 0 && (
        <div className="members-row members-row-leaders">
          {leaders.map((m) => {
            const i = idx++;
            return <MemberCard key={`${gen}-lead-${m.username}-${i}`} member={m} index={i} inView={inView} />;
          })}
        </div>
      )}

      {others.length > 0 && (
        <>
          <div className="gen-expand-row">
            <button
              type="button"
              className={`gen-expand-btn ${expanded ? 'is-open' : ''}`}
              onClick={toggleExpanded}
              aria-expanded={expanded}
            >
              <span className="gen-expand-label">
                {expanded ? 'Less' : 'More'}
              </span>
              <svg
                className="gen-expand-chevron"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key={`gen-${gen}-others`}
                className="members-row members-row-members"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
              >
                {others.map((m) => {
                  const i = idx++;
                  return <MemberCard key={`${gen}-oth-${m.username}-${i}`} member={m} index={i} inView={inView} />;
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {leaders.length === 0 && others.length === 0 && (
        <p className="gen-empty-text">Không có thành viên nào trong Gen {gen}.</p>
      )}
    </div>
  );
};

const buildGroups = (membersByGen) => {
  const collator = new Intl.Collator('vi', { sensitivity: 'base' });
  return Object.entries(membersByGen)
    .filter(([gen]) => gen !== 'unknown')
    .map(([gen, list]) => {
      const arr = list || [];
      const seen = new Set();
      const unique = arr.filter((m) => {
        const key = m.username || m.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const leaders = unique
        .filter((m) => (m.title && TITLE_ORDER[m.title]) || pickPrimaryBan(m.role) === 'ban_dieu_hanh')
        .sort((a, b) => (TITLE_ORDER[b.title] || 0) - (TITLE_ORDER[a.title] || 0));
      const leaderKeys = new Set(leaders.map((m) => m.username || m.id));
      const others = unique
        .filter((m) => !leaderKeys.has(m.username || m.id))
        .sort((a, b) => collator.compare(a.fullName || '', b.fullName || ''));
      return { gen, leaders, others };
    })
    .filter((g) => g.leaders.length > 0 || g.others.length > 0)
    .sort((a, b) => Number(a.gen) - Number(b.gen));
};

const Members = ({ lang }) => {
  const hadCache = !!_membersGroupsCache;
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true, skip: hadCache });
  // On Back navigation the data is already cached, so the scroll-triggered
  // fade-in never fires (skip=true keeps inView=false, leaving cards at
  // opacity:0). Force visibility in that case so the list shows immediately.
  const visible = inView || hadCache;
  const [groups, setGroups] = useState(() => _membersGroupsCache || []);
  const [loaded, setLoaded] = useState(() => !!_membersGroupsCache);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/members')
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const grouped = buildGroups(data.members || {});
        _membersGroupsCache = grouped;
        setGroups(grouped);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const fallbackMembers = [];
  if (loaded && groups.length === 0) {
    for (let i = 1; i <= 4; i++) {
      const name = lang[`MEMBER_${i}_NAME`];
      if (!name) break;
      fallbackMembers.push({
        fullName: name,
        position: '',
        ctfField: '',
        photos: [],
        username: `member-${i}`,
      });
    }
  }

  const hasFallback = loaded && groups.length === 0 && fallbackMembers.length > 0;

  let runningIndex = 0;

  return (
    <section className="members section" id="members" ref={ref}>
      <div className="container">
        <motion.div
          className="members-header"
          initial={{ opacity: 0, y: 30 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="terminal-section-header">
                <div className="terminal-section-titlebar">
                  root@ispclub:~/# cd /ispclub/members
                </div>
                <div className="terminal-section-body">
                  <span className="terminal-sh-line">&gt;_ sh members.sh</span>
                </div>
              </div>
          <h2 className="section-title">{lang.MEMBERS_TITLE || 'Our Team'}</h2>
          <p className="section-subtitle">
            {lang.MEMBERS_SUBTITLE || 'The talented and passionate people behind ISP Club.'}
          </p>
        </motion.div>

        {groups.map((group) => {
          const start = runningIndex;
          runningIndex += group.leaders.length + group.others.length;
          return (
            <GenSection
              key={`gen-${group.gen}`}
              group={group}
              inView={visible}
              startIndex={start}
            />
          );
        })}

        {hasFallback && (
          <div className="members-row">
            {fallbackMembers.map((member, idx) => (
              <MemberCard key={idx} member={member} index={idx} inView={visible} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Members;
